import { CLDImageAsset, CLDVideoAsset, PlayerConf, Transformation } from "_types"
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

// Parametric, SSR-safe, cache-agnostic video <source> builder.
// Single public entry point for responsive video delivery. See ./video.
export * from './video'

/**
 * @name updateCloudinarySize
 *
 * @description
 * This method refines the received URL with an updated width and height (if required).
 *
 * @param {string} url
 * @param {number} width
 * @param {number | undefined} [height]
 * @returns {string} Final url
 */
export const updateCloudinarySize = (url: string, width: number, height?: number): string => {
    if (!url) return url
    // Build transformation string
    const transformations = [`w_${width}`]
    transformations.push(`dpr_auto`)
    if (height) transformations.push(`h_${height}`)

    // Remove any existing w_ or h_ from the transformation section
    return url
        .replace(/(\/upload\/)([^/]+)?/, (_, prefix: string, trans: string = '') => {
            const filtered = trans ? trans.split(',').filter((t) => !t.startsWith('w_') && !t.startsWith('h_') && !t.startsWith('dpr_')) : []

            let finalTrans = ''
            const hasImageProcessingParams = /f_auto|q_auto|c_fill|g_face|w_|h_|dpr_/.test(trans)
            if (hasImageProcessingParams) {
                finalTrans = [...filtered, ...transformations].join(',')
            } else {
                finalTrans = [...transformations, ...filtered].join('/')
            }
            return `${prefix}${finalTrans ? finalTrans : ''}`
        })
}

export const updateCloudinaryPDImage = (image: CLDImageAsset, globalTransformation: Transformation, cloudinaryCNAME?: string): string => {
    var url = image.imageUrl;
    // Build transformation string

    if (cloudinaryCNAME && cloudinaryCNAME !== 'res.cloudinary.com' && url.indexOf('res.cloudinary.com') > 0) {
        url = url.replace('res.cloudinary.com', cloudinaryCNAME);
    }

    const srcSplitUrl = url.replace(/\/(?:(?:dpr|f|q)_[^,/]+[,/*]?)+/g, '/')
        .split('image/upload/');
    var finalUrl = srcSplitUrl[0] + 'image/upload';
    const transformationObj: Transformation[] = JSON.parse(image.transformation);
    // isTransformationOverride identify advanced video configuration on page designer
    if (image.isTransformationOverride) {
        var overridedRawTrasformations = transformationObj.map(t => t.raw_transformation).join('/')
        return finalUrl.concat('/' + overridedRawTrasformations + '/' + srcSplitUrl[1]);
    } else {
        // image.transformation, if not isTransformationOverride contains preference data configuration at the moment element was inserted in page
        // so transformation should be taken from preferences to be updated with last values
        // anyway in raw_transformation could be contained parameters applied during insertion that should be mantained 
        var rawTransformations = transformationObj.map(t => t.raw_transformation)
        const transformations: string[] = [];
        if (globalTransformation.dpr && globalTransformation.dpr !== 'none') {
            transformations.push(`dpr_${globalTransformation.dpr}`);
        }   
              
        if (globalTransformation.fetchFormat && globalTransformation.fetchFormat !== 'none') {
            transformations.push(`f_${globalTransformation.fetchFormat}`);
        }    

        if (globalTransformation.quality && globalTransformation.quality !== 'none') {
            transformations.push(`q_${globalTransformation.quality}`);
        }

        finalUrl = finalUrl.concat('/' + transformations.join(','))
        if (rawTransformations.length && rawTransformations[0]){
            finalUrl = finalUrl.concat('/' + rawTransformations.join('/'))
        }
        return finalUrl.concat('/' + srcSplitUrl[1])
    }
}

export const updateCloudinaryPDVideo = (video: CLDVideoAsset, globalTransformation: Transformation, cloudinaryCNAME?: string): string => {
    const playerConf: PlayerConf = JSON.parse(video.playerConf);
    const sourceConfig = playerConf?.sourceConfig;
    var url = sourceConfig.video.asset.secure_url; // it contains extension (video.url does not)
    // Build transformation string
    if (cloudinaryCNAME && cloudinaryCNAME !== 'res.cloudinary.com' && url.indexOf('res.cloudinary.com') > 0) {
        url = url.replace('res.cloudinary.com', cloudinaryCNAME);
    }
    const srcSplitUrl = url.replace(/\/(?:(?:br|f|q)_[^,/]+[,/*]?)+/g, '/')
        .split('video/upload/');
    var finalUrl = srcSplitUrl[0] + 'video/upload';
    // isTransformationOverride identify advanced video configuration on page designer
    if (playerConf.isTransformationOverride) {
        var overridedRawTrasformations = sourceConfig.transformation.map(t => t.raw_transformation).join('/')
        return finalUrl.concat('/' + overridedRawTrasformations + '/' + srcSplitUrl[1]);
    } else {
        var rawTransformations = sourceConfig.transformation.map(t => t.raw_transformation)
        const transformations: string[] = [];
        if (globalTransformation.bit_rate && globalTransformation.bit_rate !== 'none' && globalTransformation.bit_rate !== 'default') {
            transformations.push(`br_${globalTransformation.bit_rate}`);
        }   
        // without cloudinary integration url with dynamic format not contains extension, prevent using format with malformed url  
        // if (globalTransformation.fetchFormat && globalTransformation.fetchFormat !== 'none') {
        //     transformations.push(`f_${globalTransformation.fetchFormat}`);
        // }    

        if (globalTransformation.quality && globalTransformation.quality !== 'none') {
            transformations.push(`q_${globalTransformation.quality}`);
        }

        finalUrl = finalUrl.concat('/' + transformations.join(','))
        if (rawTransformations.length && rawTransformations[0]){
            finalUrl = finalUrl.concat('/' + rawTransformations.join('/'))
        }
        return finalUrl.concat('/' + srcSplitUrl[1])
    }
}

// BASIC poster handling (cost reduction).
// The poster (a still frame off the video/upload resource, billed by Cloudinary as an IMAGE
// transformation) is visible for at most a sub-second before the autoplay-muted video starts,
// so it does not need full resolution, high quality, or per-browser format negotiation.
//
// Previously the poster passed through `CloudinaryVideoPosterTransformations = f_auto,q_auto`
// at the master frame resolution: `f_auto` fanned out one derived image PER negotiated format
// (WebP/AVIF/JPEG) → ~3 billable derivations per poster, each uncapped in size.
//
// Now we deliver a single explicit transformation, collapsing ~3 derivations to 1 and sizing
// the poster to its box WITHOUT client-side interpolation. Shared delivery params:
//   - f_jpg (NOT f_auto): one explicit format, no per-browser fan-out. JPG chosen over WebP
//     because it is universally decodable and, for a briefly-shown placeholder, the WebP size edge
//     is irrelevant; the goal is the fewest derivations, not the smallest single file.
//   - q_auto:eco: eco quality is fine for a placeholder shown briefly before playback.
//   - c_limit: never upscales past the master, so smaller-box modules are capped down automatically.
//
// The width is per-VARIANT, because the poster is a SINGLE image (one `poster=` attribute, not a
// responsive source set) — we do NOT want N posters for N DPRs, just one at DPR1 for the box size:
//   - desktop (w_1920): worst case is the full-bleed hero (~1920 CSS desktop, up to ~2560 on large
//     screens). 1920 covers the desktop box 1:1 at DPR1; on DPR2 the browser DOWNSCALES (always
//     sharp) rather than upscaling a small poster into a large box (the interpolation we avoid).
//   - mobile (w_440): mobile boxes are ~440 CSS wide. Serving 1920 to a phone is pure waste; 440 at
//     DPR1 matches the box. This matters most where a module has SEPARATE desktop/mobile assets
//     (hero-banner, wall-mixed): the mobile <Video> must get the mobile-sized poster, not 1920.
// Still ONE derivation per variant → transformation count unchanged, only the mobile file shrinks.
export const POSTER_BASIC_TRANSFORMATION = 'f_jpg,q_auto:eco,w_1920,c_limit';
export const POSTER_BASIC_TRANSFORMATION_MOBILE = 'f_jpg,q_auto:eco,w_440,c_limit';

export const updateCloudinaryPDPoster = (
    video: CLDVideoAsset,
    _globalTransformation: Transformation,
    cloudinaryCNAME?: string,
    variant: 'desktop' | 'mobile' = 'desktop',
): string => {
    const playerConf: PlayerConf = JSON.parse(video.playerConf);
    const sourceConfig = playerConf?.sourceConfig;
    var url = sourceConfig.poster;
    if (cloudinaryCNAME && cloudinaryCNAME !== 'res.cloudinary.com' && url.indexOf('res.cloudinary.com') > 0) {
        url = url.replace('res.cloudinary.com', cloudinaryCNAME);
    }
    // Strip any existing dpr_/f_/q_ delivery params, then split on the upload segment so we can
    // preserve the author's version/public_id tail (and any raw author transformations in it).
    const srcSplitUrl = url.replace(/\/(?:(?:dpr|f|q)_[^,/]+[,/*]?)+/g, '/')
        .split('video/upload/');
    var finalUrl = srcSplitUrl[0] + 'video/upload';
    // Emit the basic explicit transformation for the given variant (ignores the
    // CloudinaryVideoPosterTransformations preference, which with f_auto,q_auto is now redundant).
    const transformation = variant === 'mobile' ? POSTER_BASIC_TRANSFORMATION_MOBILE : POSTER_BASIC_TRANSFORMATION
    finalUrl = finalUrl.concat('/' + transformation)

    return finalUrl.concat('/' + srcSplitUrl[1])
}